import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Lock, Trash2, UploadCloud } from "lucide-react";
import { IMAGE_SLOTS } from "../data/content";
import { TextsPanel } from "../components/admin/TextsPanel";
import { RequestsPanel } from "../components/admin/RequestsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
    const [token, setToken] = useState(
        () => localStorage.getItem("ta_admin_token") || "",
    );
    const [password, setPassword] = useState("");
    const [images, setImages] = useState({});
    const [busy, setBusy] = useState("");
    const [checking, setChecking] = useState(false);
    const [tab, setTab] = useState("imagens");
    const fileRefs = useRef({});

    const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

    const loadImages = async () => {
        try {
            const r = await axios.get(`${API}/images`);
            setImages(r.data.images || {});
        } catch {
            /* sem ligação */
        }
    };

    useEffect(() => {
        if (token) loadImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setChecking(true);
        try {
            const r = await axios.post(`${API}/admin/login`, { password });
            localStorage.setItem("ta_admin_token", r.data.token);
            setToken(r.data.token);
            toast.success("Sessão iniciada.");
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Falha na autenticação.");
        } finally {
            setChecking(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("ta_admin_token");
        setToken("");
    };

    const handle401 = (err) => {
        if (err.response?.status === 401) {
            logout();
            toast.error("Sessão expirada. Inicie sessão novamente.");
            return true;
        }
        return false;
    };

    const upload = async (slot, file) => {
        if (!file) return;
        setBusy(slot);
        try {
            const fd = new FormData();
            fd.append("ficheiro", file);
            await axios.post(`${API}/admin/images/${slot}`, fd, auth());
            await loadImages();
            toast.success("Imagem atualizada no site.");
        } catch (err) {
            if (!handle401(err)) toast.error("Falha no upload. Tente novamente.");
        } finally {
            setBusy("");
        }
    };

    const reset = async (slot) => {
        setBusy(slot);
        try {
            await axios.delete(`${API}/admin/images/${slot}`, auth());
            await loadImages();
            toast.success("Imagem reposta para a fotografia padrão.");
        } catch (err) {
            if (!handle401(err)) toast.error("Falha ao repor imagem.");
        } finally {
            setBusy("");
        }
    };

    if (!token) {
        return (
            <section
                className="flex min-h-screen items-center justify-center px-6 pt-20"
                data-testid="admin-login"
            >
                <form
                    onSubmit={login}
                    className="w-full max-w-sm border border-white/10 bg-coal p-10"
                    data-testid="admin-login-form"
                >
                    <span className="flex h-12 w-12 items-center justify-center bg-brand">
                        <Lock className="h-5 w-5 text-white" />
                    </span>
                    <h1 className="mt-6 font-display text-2xl font-black uppercase tracking-tight">
                        Gestão de Imagens
                    </h1>
                    <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                        Área reservada — Transportes Alzira
                    </p>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Palavra-passe"
                        data-testid="admin-password-input"
                        className="mt-8 h-12 w-full border border-white/15 bg-steel px-4 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-brand"
                    />
                    <button
                        type="submit"
                        disabled={checking}
                        data-testid="admin-login-button"
                        className="mt-4 flex w-full items-center justify-center gap-2 bg-brand py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-ink disabled:opacity-60"
                    >
                        {checking && <Loader2 className="h-4 w-4 animate-spin" />}
                        Iniciar sessão
                    </button>
                </form>
            </section>
        );
    }

    const groups = [...new Set(IMAGE_SLOTS.map((s) => s.group))];

    return (
        <section
            className="mx-auto max-w-7xl px-6 pb-28 pt-36 lg:px-10"
            data-testid="admin-panel"
        >
            <div className="flex flex-col justify-between gap-6 border-b border-white/10 pb-10 sm:flex-row sm:items-end">
                <div>
                    <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand">
                        Área reservada
                    </p>
                    <h1 className="mt-4 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">
                        Gestão do <span className="text-brand">site</span>
                    </h1>
                    <p className="mt-4 max-w-xl text-sm text-zinc-400">
                        Gira as imagens, os textos e os pedidos de orçamento do
                        site. As alterações aplicam-se de imediato.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {[
                        ["imagens", "Imagens"],
                        ["textos", "Textos"],
                        ["pedidos", "Pedidos"],
                    ].map(([id, label]) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            data-testid={`admin-tab-${id}`}
                            className={`border px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                                tab === id
                                    ? "border-brand bg-brand text-white"
                                    : "border-white/20 text-zinc-400 hover:border-white/40 hover:text-white"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                <button
                    onClick={logout}
                    data-testid="admin-logout-button"
                    className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-brand"
                >
                    <LogOut className="h-4 w-4" />
                    Sair
                </button>
                </div>
            </div>

            {tab === "imagens" && groups.map((g) => (
                <div key={g} className="mt-16">
                    <h2 className="font-mono text-xs uppercase tracking-[0.35em] text-zinc-500">
                        {g}
                    </h2>
                    <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {IMAGE_SLOTS.filter((s) => s.group === g).map((s) => {
                            const custom = images[s.key];
                            const src = custom
                                ? `${BACKEND_URL}${custom}`
                                : s.fallback;
                            return (
                                <div
                                    key={s.key}
                                    className="border border-white/10 bg-coal"
                                    data-testid={`admin-slot-${s.key}`}
                                >
                                    <div className="relative overflow-hidden">
                                        <img
                                            src={src}
                                            alt={s.label}
                                            loading="lazy"
                                            className="aspect-[16/10] w-full object-cover"
                                        />
                                        {custom && (
                                            <span className="absolute left-3 top-3 bg-brand px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-white">
                                                Foto real
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-5">
                                        <p className="font-display text-base font-bold uppercase tracking-tight">
                                            {s.label}
                                        </p>
                                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                            {custom ? "Personalizada" : "Fotografia padrão"}
                                        </p>
                                        <div className="mt-4 flex gap-2">
                                            <button
                                                onClick={() =>
                                                    fileRefs.current[s.key]?.click()
                                                }
                                                disabled={busy === s.key}
                                                data-testid={`admin-upload-${s.key}`}
                                                className="inline-flex flex-1 items-center justify-center gap-2 bg-brand px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-ink disabled:opacity-60"
                                            >
                                                {busy === s.key ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <UploadCloud className="h-3.5 w-3.5" />
                                                )}
                                                {custom ? "Trocar" : "Carregar foto"}
                                            </button>
                                            {custom && (
                                                <button
                                                    onClick={() => reset(s.key)}
                                                    disabled={busy === s.key}
                                                    data-testid={`admin-reset-${s.key}`}
                                                    className="inline-flex items-center justify-center border border-white/20 px-3 text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-brand disabled:opacity-60"
                                                    aria-label="Repor imagem padrão"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            ref={(el) => (fileRefs.current[s.key] = el)}
                                            onChange={(e) => {
                                                upload(s.key, e.target.files?.[0]);
                                                e.target.value = "";
                                            }}
                                            data-testid={`admin-file-${s.key}`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {tab === "textos" && <TextsPanel auth={auth} />}
            {tab === "pedidos" && <RequestsPanel auth={auth} />}
        </section>
    );
}
